
import React from "react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface LoadingOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  isLoading: boolean;
  children: React.ReactNode;
  loadingText?: string;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "blur" | "fade";
  spinnerOnly?: boolean;
  fullScreen?: boolean;
}

export function LoadingOverlay({
  isLoading,
  children,
  loadingText = "Loading...",
  size = "md",
  variant = "default",
  spinnerOnly = false,
  fullScreen = false,
  className,
  ...props
}: LoadingOverlayProps) {
  // Return just children if not loading
  if (!isLoading) {
    return <>{children}</>;
  }
  
  // Define spinner sizes
  const spinnerSizeMap = {
    sm: "h-4 w-4",
    md: "h-8 w-8",
    lg: "h-12 w-12"
  };
  
  // Define full-screen container classes
  const containerClasses = fullScreen ? "fixed inset-0 z-50" : "relative";
  
  // Define variant-specific classes
  const variantClasses = {
    default: "bg-background/80",
    blur: "backdrop-blur-sm bg-background/50",
    fade: "bg-background/80 animate-in fade-in duration-300"
  };
  
  // If spinner only, return just the spinner
  if (spinnerOnly) {
    return (
      <div className={cn(
        containerClasses,
        "flex items-center justify-center",
        className
      )} {...props}>
        <Loader2 className={cn(
          spinnerSizeMap[size],
          "animate-spin text-primary"
        )} />
      </div>
    );
  }
  
  // Return the full overlay
  return (
    <div className="relative">
      {children}
      
      <div className={cn(
        "absolute inset-0 flex flex-col items-center justify-center",
        variantClasses[variant],
        className
      )} {...props}>
        <Loader2 className={cn(
          spinnerSizeMap[size],
          "animate-spin text-primary mb-2"
        )} />
        {loadingText && (
          <p className="text-sm text-muted-foreground">{loadingText}</p>
        )}
      </div>
    </div>
  );
}

// Removed the redundant named export at the bottom that was causing the error
